import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { Landing } from './features/landing/Landing';
import { Home } from './features/home/Home';
import { Services } from './features/services/Services';
import { About } from './features/about/About';
import { Gallery } from './features/gallery/Gallery';
import { Booking } from './features/booking/Booking';
import { BookChoice } from './features/booking/BookChoice';
import { Contact } from './features/contact/Contact';
import { Stay } from './features/stay/Stay';
import { RoomBookingPage } from './features/stay/RoomBookingPage';
import { Cars } from './features/cars/Cars';
import { CarBookingPage } from './features/cars/CarBookingPage';
import { GiftCards } from './features/giftcards/GiftCards';
import { AdminLogin } from './features/admin/AdminLogin';
import { ProtectedRoute } from './features/admin/ProtectedRoute';
import { useAuth, can, ACCESS } from './lib/auth';
import { AdminLayout } from './features/admin/AdminLayout';
import { Dashboard } from './features/admin/Dashboard';
import { Bookings } from './features/admin/Bookings';
import { Settings } from './features/admin/Settings';
import { Users } from './features/admin/Users';
import { Clients } from './features/admin/Clients';
import { Treatments } from './features/admin/Treatments';
import { Invoices } from './features/admin/Invoices';
import { GiftCards as AdminGiftCards } from './features/admin/GiftCards';
import { Stays } from './features/admin/Stays';
import { CarHire } from './features/admin/CarHire';
import { Fleet } from './features/admin/Fleet';
import { Images } from './features/admin/Images';
import { Account } from './features/admin/Account';
import { Splash } from './components/Splash';

function Gate({ area, children }: { area: keyof typeof ACCESS; children: React.ReactNode }) {
  const { role } = useAuth();
  if (!can(role, area)) return <Navigate to={role === 'staff' ? '/admin/bookings' : '/admin'} replace />;
  return <>{children}</>;
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Navigation />
      <main>{children}</main>
      <Footer />
    </>
  );
}

export default function App() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');

  if (isAdmin) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Gate area="dashboard"><Dashboard /></Gate>} />
            <Route path="bookings" element={<Gate area="bookings"><Bookings /></Gate>} />
            <Route path="settings" element={<Gate area="settings"><Settings /></Gate>} />
            <Route path="users" element={<Gate area="users"><Users /></Gate>} />
            <Route path="clients" element={<Gate area="clients"><Clients /></Gate>} />
            <Route path="treatments" element={<Gate area="treatments"><Treatments /></Gate>} />
            <Route path="invoices" element={<Gate area="invoices"><Invoices /></Gate>} />
            <Route path="giftcards" element={<Gate area="giftcards"><AdminGiftCards /></Gate>} />
            <Route path="stays" element={<Gate area="stays"><Stays /></Gate>} />
            <Route path="carhire" element={<Gate area="carhire"><CarHire /></Gate>} />
            <Route path="fleet" element={<Gate area="fleet"><Fleet /></Gate>} />
            <Route path="images" element={<Gate area="images"><Images /></Gate>} />
            <Route path="account" element={<Account />} />
          </Route>
        </Routes>
      </>
    );
  }

  return (
    <>
      <Splash />
      <ScrollToTop />
      <PublicLayout>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/spa" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/book" element={<BookChoice />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/stay" element={<Stay />} />
          <Route path="/stay/book" element={<RoomBookingPage />} />
          <Route path="/cars" element={<Cars />} />
          <Route path="/cars/book" element={<CarBookingPage />} />
          <Route path="/gift-cards" element={<GiftCards />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </PublicLayout>
    </>
  );
}
